import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Quick refills of documented medications (EXECDEV-101, migration 20261014203928). A refill is a
 * printable prescription signed with operation "renew"; each medication on it may name the signed
 * prescription item it renews. Inside a visit it signs on that encounter. Outside one, the server
 * opens a "Prescription renewal" encounter, signs the prescription and a one-line note on it, and
 * closes it in one step.
 */
/** A prescription lists at most ten medications, so an item index is 0–9. */
const ItemIndexSchema = z.number().int().min(0).max(9);
/** What a renewed medication names: the signed prescription item it renews. */
export const PrescriptionRenewalLinkSchema = z
    .object({ printablePrescriptionId: IdSchema, itemIndex: ItemIndexSchema })
    .strict();
/**
 * The latest prescription item signed at this site that lists a chart medication, as it was
 * signed. The database checks only a signed item's name, so a field an older prescription stored
 * in another shape is dropped rather than refusing the list; the physician fills it in. The
 * structured dose is checked where it is used.
 */
export const MedicationRefillPrefillSchema = z
    .object({
    printablePrescriptionId: IdSchema,
    itemIndex: ItemIndexSchema,
    encounterId: IdSchema,
    signedAt: IsoDateTimeSchema,
    item: z
        .object({
        medicationDisplay: z.string().min(1),
        sig: z.string().optional().catch(undefined),
        quantity: z.number().optional().catch(undefined),
        quantityUnit: z.string().optional().catch(undefined),
        refills: z.number().int().optional().catch(undefined),
        indication: z.string().optional().catch(undefined),
        substitutionAllowed: z.boolean().optional().catch(undefined),
        daysSupply: z.number().int().optional().catch(undefined),
        medicationCode: z.string().optional().catch(undefined),
        medicationCodeSystem: z.string().optional().catch(undefined),
        structuredSig: z.unknown().optional(),
    })
        .strict(),
})
    .strict();
/** An active chart medication a physician can refill. */
export const MedicationRefillCandidateSchema = z
    .object({
    /** The chart entry's current version (clinical.medications head). */
    medicationId: IdSchema,
    version: z.number().int().positive(),
    display: z.string().min(1),
    sig: z.string().optional(),
    code: z.string().optional(),
    codeSystem: z.string().optional(),
    /** Where the chart entry came from (clinician_prescribed, patient_reported, …). */
    sourceKind: z.string().optional(),
    recordedAt: IsoDateTimeSchema,
    /** Absent when no prescription signed at this site lists it. */
    lastPrescription: MedicationRefillPrefillSchema.optional(),
})
    .strict();
/** GET /v1/patients/:patientId/medication-refills: newest chart entry first. */
export const MedicationRefillCandidatesSchema = z
    .object({ patientId: IdSchema, items: z.array(MedicationRefillCandidateSchema) })
    .strict();
/**
 * The "Prescription renewal" encounter a refill outside a visit opened, signed and closed.
 * `created` is false when a retried idempotency key answered with the renewal it already signed.
 */
export const PrescriptionRenewalVisitSchema = z
    .object({ encounterId: IdSchema, noteId: IdSchema, created: z.boolean() })
    .strict();
//# sourceMappingURL=medication-refills.js.map